import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { apiClient } from '@/services/apiClient';
import { LoadingPlaceholder } from '@/components/LoadingPlaceholder';

export interface FeatureFlags {
  postingLog: boolean;
  auditTrail: boolean;
  hoursToComplete: boolean;
}

export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  postingLog: true,
  auditTrail: true,
  hoursToComplete: true,
};

// Consumers outside a provider (demo, tests) see every feature enabled.
const FeatureFlagContext = createContext<FeatureFlags>(DEFAULT_FEATURE_FLAGS);

type FeatureFlagsResponse = Partial<Record<keyof FeatureFlags, boolean | null>>;

// Only an explicit `false` disables a feature; missing or malformed values keep it enabled.
function normaliseFlags(response: FeatureFlagsResponse | null | undefined): FeatureFlags {
  return {
    postingLog: response?.postingLog !== false,
    auditTrail: response?.auditTrail !== false,
    hoursToComplete: response?.hoursToComplete !== false,
  };
}

/**
 * Provides config-driven feature flags. Flags are fetched from the API only in live mode
 * (app-prod, where AuthProvider.isProduction is true); everywhere else all features stay enabled.
 * Values are held in memory only.
 */
export function FeatureFlagProvider({ children }: { children: React.ReactNode }) {
  const { isProduction } = useAuth();
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
  const [isLoading, setIsLoading] = useState<boolean>(isProduction === true);

  useEffect(() => {
    if (isProduction !== true) {
      setFlags(DEFAULT_FEATURE_FLAGS);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    apiClient
      .get<FeatureFlagsResponse>('/feature-flags')
      .then((response) => {
        if (!cancelled) setFlags(normaliseFlags(response));
      })
      .catch((error: unknown) => {
        console.error('[FeatureFlagContext] Failed to load feature flags:', error);
        if (!cancelled) setFlags(DEFAULT_FEATURE_FLAGS);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isProduction]);

  if (isLoading) {
    return (
      <LoadingPlaceholder
        message="Setting up your workspace"
        description="Authenticating and loading your data..."
      />
    );
  }

  return <FeatureFlagContext.Provider value={flags}>{children}</FeatureFlagContext.Provider>;
}

export function useFeatureFlags(): FeatureFlags {
  return useContext(FeatureFlagContext);
}
